import { Descriptions, Select } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { CgCloseR } from "react-icons/cg";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getSteps } from "../../../api/courses.api";
import { getSubjects } from "../../../api/subject.api";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetSteps } from "../../../models/courses.model";
import { IArchive } from "../../../models/homepageManagementStudyArchive.model";
import { IGetSubjects } from "../../../models/subject.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import ErrorResponse from "../../ErrorResponse";
import FileUpload from "../../FileUpload";
import Loader from "../../Loader";
import Tinymce from "../../Tinymce";

export interface IProps {
  data?: IArchive|undefined;
  submit: (data: FormData) => void;
}

const ArchiveEditor: FC<IProps> = ({
  data,
  submit,
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { infoNotification } = useNotification();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { data: res, isLoading, error, execute } = useAxios<IGetSteps>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSubjects>();

  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState<any>([]);
  const [courseCode, setCourseCode] = useState<number>();
  const [subjectCode, setSubjectCode] = useState<number>();
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [readCount, setReadCount] = useState(0);
  const [isCreate, setIsCreate] = useState(true);
  const [attachFiles, setAttachFiles] = useState<any>();

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setCourseCode(data.courseCode);
      setSubjectCode(data.subjectCode);
      setContent(data.content);
      setAttachFiles(data.files);
      setReadCount(data.readCount);
      setIsCreate(false);
    }
  }, [data]);

  useEffect(() => {
    execute(getSteps());
    execute2(getSubjects());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const _steps: any = [];
      res.data?.forEach((level) => {
        level.step?.forEach((step) => {
          _steps.push({
            label: step.courseName,
            value: step.courseCode,
          });
        });
      });
      setSteps(_steps);
    }
  }, [res, isLoading]);

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (courseCode !== 0 && !courseCode) {
      return infoNotification(t('homepage.archive.message2'));
    }
    if (subjectCode !== 0 && !subjectCode) {
      return infoNotification(t('homepage.archive.message3'));
    }
    if (!content) {
      return infoNotification(t('homepage.archive.message4'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('courseCode', String(courseCode));
    form.append('subjectCode', String(subjectCode));
    form.append('content', content);

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    } else {
      /* 수정 시 파일 처리 */
      if (data?.attachFileCode) {
        form.append('attachFileCode', String(data.attachFileCode));
      }

      if (attachFiles) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        attachFiles.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('deleteFile', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('fileStatus', 'update');
        else if (isAllDeleted) form.append('fileStatus', 'delete');
        else if (hasDeleted) form.append('fileStatus', 'update');
        else form.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('fileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('fileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('newFiles', item);
        });
      }
    }

    submit(form);    
  }

  const onDeleteAttachedFile = (index: number) => {
    const _attachFiles = [...attachFiles];
    _attachFiles[index].originalName = null;
    setAttachFiles(_attachFiles);
  }

  return (
    <div className='comm-table__area rowType mt-0'>
      {isLoading || isLoading2 ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />

      <Descriptions
        bordered
        column={5}
        size="small"
      >
        <Descriptions.Item label={t('common.column.title')} span={5}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column2')}>
          <Select
            className="w-100"
            placeholder={t('common.button.select')}
            value={courseCode}
            onChange={value => setCourseCode(value)}
          >
            <Select.Option value={0}>ALL</Select.Option>
            {steps?.map((item: any) => (
              <Select.Option
                key={item.value}
                value={item.value}
              >
                {item.label}
              </Select.Option>
            ))}
          </Select>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column3')}>
          <Select
            className="w-100"
            placeholder={t('common.button.select')}
            value={subjectCode}
            onChange={value => setSubjectCode(value)}
          >
            <Select.Option value={0}>{`(${t('homepage.archive.option1')})`}</Select.Option>
            {res2?.data?.map(item => (
              <Select.Option
                key={item.subjectCode}
                value={item.subjectCode}
              >
                {item.subjectName}
              </Select.Option>
            ))}
          </Select>
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.writer')}>
          {auth?.korName}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column8')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column7')}>
          {readCount}
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.contents')}`} span={5}>
          <Tinymce
            content={content}
            setContent={setContent}
          />
        </Descriptions.Item>
        {
          attachFiles?.length
            ? (
              <Descriptions.Item label={t('common.column.existFile')} span={3}>
                {
                  attachFiles.map((item: any, index: number) => {
                    return item?.originalName
                      ? (
                        <div>
                          <a
                            className="download-link"
                            href={`${item.fileLocation}`}
                            download={item.originalName}
                          >
                            {item.originalName}
                          </a>
                          <span
                            className="ml-1 pointer"
                            onClick={() => onDeleteAttachedFile(index)}
                          >
                            <CgCloseR />
                          </span>
                        </div>
                      )
                      : null;
                  })
                }                
              </Descriptions.Item>
              )
            : null
        }        
        <Descriptions.Item label={isCreate ? t('common.column.file') : t('common.column.additFile')} span={5}>
          {
            files.length > 0
              ? files.map((file: any, index: number) => (
                <div className="flex mb-2" key={index}>
                  <FileUpload
                    file={files[index]}
                    setFile={(file: any) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small gray ml-2"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                    />
                  </div>
                </div>
              ))
              : null
          }
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddFile}
              text={t('common.button.add')}
            />
          </div>
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/homepage/archive${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </div>
  );
}

export default ArchiveEditor;
